import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Button from "@/components/Button";
import Input from "@/components/Input";
import Select from "@/components/Select";
import Icons from "@/assets/icons";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { ApiService } from "@/services/ApiService";
import { messageService } from "@/services/MessageService";
import { validateFormikFields } from "@/lib/utils";
import RegisterResourceSchema from "./Schema/RegisterResourceSchema";
import { useEffect, useState } from "react";

interface ResourceDialogProps {
  open: boolean;
  onClose: () => void;
  resourceToEdit?: any | null;
  onSuccess: () => void;
}

const ResourceDialog = ({ open, onClose, resourceToEdit, onSuccess }: ResourceDialogProps) => {
  const { t } = useTranslation();
  const { post, patch } = ApiService;
  const [schema] = useState(RegisterResourceSchema(t));
  const [tipoRecursoOptions, setTipoRecursoOptions] = useState<any[]>([]);

  const initialValues = {
    nomeRecurso: "",
    tipoRecurso: "",
    status: "ATIVO",
  };

  const formik = useFormik({
    initialValues,
    validate: (values) => validateFormikFields(values, schema),
    onSubmit: async (values) => {
      try {
        const request = resourceToEdit?.id
          ? patch(`api/v1/recurso/atualizar/${resourceToEdit.id}`, values)
          : post("api/v1/recurso/registrar", values);

        const { status } = await request;

        if (status === 200) {
          messageService.success(
            resourceToEdit ? t("resourcesForm.messages.update.title") : t("resourcesForm.messages.save.title"),
            resourceToEdit ? t("resourcesForm.messages.update.description") : t("resourcesForm.messages.save.description")
          );
          onSuccess();
          handleClose();
        }
      } catch (error: any) {
        // Tratamento de erro específico para conflito de status (licença ativa)
        if (error.response?.status === 409) {
            messageService.error(
                t("resourcesForm.messages.error.title", "Erro"),
                error.response.data?.message || error.message
            );
        } else {
            console.error(error);
        }
      }
    },
  });

  useEffect(() => {
    setTipoRecursoOptions([{ title: t("resourcesForm.inputs.select.tipoRecurso.value"), value: "NUMERICO" }]);
  }, []);

  useEffect(() => {
    if (open) {
      if (resourceToEdit) {
        formik.setValues({
          nomeRecurso: resourceToEdit.nomeRecurso || "",
          tipoRecurso: resourceToEdit.tipoRecurso || "",
          status: resourceToEdit.status || "ATIVO",
        });
      } else {
        formik.resetForm();
      }
    }
  }, [open, resourceToEdit]);

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const statusOptions = [
    { title: t("table.user.body.active.true"), value: "ATIVO" },
    { title: t("table.user.body.active.false"), value: "INATIVO" },
  ];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] bg-white p-6 rounded-lg">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-gray-800 mb-4">
            {resourceToEdit ? t("resourcesForm.titleEdit") : t("resourcesForm.title")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={formik.handleSubmit} className="flex flex-col gap-6">
          <Input
            id="nomeRecurso"
            label={t("resourcesForm.inputs.nomeRecurso.label")}
            placeholder={t("resourcesForm.inputs.nomeRecurso.placeholder", "Nome do recurso")}
            error={
              formik.errors.nomeRecurso && formik.touched.nomeRecurso
                ? formik.errors.nomeRecurso
                : ""
            }
            {...formik.getFieldProps("nomeRecurso")}
          />

          <Select
            id="tipoRecurso"
            label={t("resourcesForm.inputs.select.tipoRecurso.label")}
            placeholder={t("resourcesForm.inputs.select.tipoRecurso.placeholder")}
            options={tipoRecursoOptions}
            error={
              formik.errors.tipoRecurso && formik.touched.tipoRecurso
                ? formik.errors.tipoRecurso
                : ""
            }
            {...formik.getFieldProps("tipoRecurso")}
          />

          <Select
            id="status"
            label={t("table.user.head.active", "Status")}
            placeholder="Selecione o status"
            options={statusOptions}
            error={
              formik.errors.status && formik.touched.status
                ? (formik.errors.status as string)
                : ""
            }
            {...formik.getFieldProps("status")}
          />

          <div className="flex justify-end gap-3 mt-4">
            <Button
              label={t("resourcesForm.buttons.cancel")}
              color="neutro"
              onClick={handleClose}
              type="button"
              icon={<Icons.Close pathStyle="fill-inherit" />}
            />
            <Button
              label={t("resourcesForm.buttons.save")}
              color="primario"
              type="submit"
              disabled={formik.isSubmitting}
              icon={<Icons.Save pathStyle="fill-inherit" />}
            />
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ResourceDialog;